<template>
  <div class="flex flex-row shadow-md gap-2 rounded-xl mb-4 bg-white p-1">
    <button class="subtab" @click="go(RouteName.Events)">
      {{ t("events.list.list") }}
    </button>
    <button class="subtab active">
      {{ t("events.detail.detail") }}</button
    ><button
      class="subtab"
      :class="
        (eventDetail ?? false) &&
        (eventDetail!.status.value == EventState.events[4].value ||
          eventDetail!.status.value == EventState.events[5].value)
          ? 'inactive'
          : ''
      "
      @click="go(RouteName.EventDetailEdit, { id: id })"
      :disabled="
        (eventDetail ?? false) &&
        (eventDetail!.status.value == EventState.events[4].value ||
          eventDetail!.status.value == EventState.events[5].value)
      "
    >
      {{ t("events.edit.edit") }}
    </button>
  </div>
  <div v-if="isLoading" class="flex h-[60vh] justify-center">
    <LoadingIcon :isLoading="isLoading" />
  </div>
  <div class="flex flex-col gap-4" v-if="!isLoading && eventDetail">
    <div class="flex flex-row justify-between">
      <div class="flex flex-col gap-2">
        <h1 class="font-extrabold text-2xl">{{ t("events.detail.title") }}</h1>
        <div class="text-light-gray text-sm">
          {{ eventDetail.name }}
        </div>
      </div>
      <div class="pb-3">
        <CustomButton
          @click="downloadPDF"
          :title="t('events.detail.downloadPDF')"
          :is-loading="isDownloadingPDF"
          button-styles="bg-white! text-gray-600! text-xs!"
        >
          <IconManager name="DownloadFile" />
        </CustomButton>
      </div>
    </div>
    <div class="grid grid-cols-6 gap-3 my-4">
      <InfoBox
        :title="t('events.detail.status')"
        :value="eventDetail.status.getLocalization()"
        backgroundColor="#EFF4FF"
        textColor="#2563EB"
      >
        <IconManager name="Calendar" class="text-[#2563EB]" />
      </InfoBox>

      <InfoBox
        :title="t('events.detail.type')"
        :value="eventDetail.type"
        backgroundColor="#FEF3E2"
        textColor="#D97706"
      >
        <IconManager name="Search" class="text-[#D97706]" />
      </InfoBox>

      <InfoBox
        :title="t('events.detail.startDate')"
        :value="getLocalShortDateWithTime(eventDetail.startDate)"
        backgroundColor="#F4EEFE"
        textColor="#7C3AED"
      >
        <IconManager name="Clock" class="text-[#7C3AED]" />
      </InfoBox>

      <InfoBox
        :title="t('events.detail.endDate')"
        :value="getLocalShortDateWithTime(eventDetail.endDate)"
        backgroundColor="#E6F5F3"
        textColor="#0D9488"
      >
        <IconManager name="Clock" class="text-[#0D9488]" />
      </InfoBox>

      <InfoBox
        :title="t('events.detail.numberOfParticipants')"
        :value="eventDetail.numberOfParticipants"
        :backgroundColor="EventState.events[3].lightColor"
        :textColor="EventState.events[3].color"
      >
        <IconManager
          name="Users"
          :style="{ color: EventState.events[3].color }"
        />
      </InfoBox>

      <InfoBox
        :title="t('events.detail.expense')"
        :value="eventDetail.budget"
        backgroundColor="#E8F6EE"
        textColor="var(--color-primary)"
      >
        <IconManager name="Flag" class="text-primary" />
      </InfoBox>
    </div>
    <div class="flex flex-row gap-4">
      <div class="flex flex-col bg-white rounded-xl shadow-md p-4 w-full">
        <label class="font-extrabold">
          {{ t("events.detail.generalInformation") }}
        </label>
        <div class="resource-info-row">
          <span class="title">{{ t("common.name") }}</span>
          <span class="value">{{ eventDetail.name }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("events.detail.creationDate") }}</span>
          <span class="value">{{
            getLocalShortDate(eventDetail.creationDate)
          }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("events.detail.startDate") }}</span>
          <span class="value">{{
            getLocalShortDate(eventDetail.startDate)
          }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("events.detail.endDate") }}</span>
          <span class="value">{{
            getLocalShortDate(eventDetail.endDate)
          }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{
            t("events.detail.numberOfParticipants")
          }}</span>
          <span class="value">{{ eventDetail.numberOfParticipants }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("events.detail.budget") }}</span>
          <span class="value">{{ eventDetail.budget }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("events.detail.expense") }}</span>
          <span class="value">{{ eventDetail.expense }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("events.detail.requestingService") }}</span>
          <span class="value">{{ eventDetail.requestingService }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("events.detail.team") }}</span>
          <span class="value">{{ eventDetail.team }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("events.detail.location") }}</span>
          <span class="value">{{ eventDetail.location }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("events.detail.venueType") }}</span>
          <span class="value">{{ eventDetail.venueType }}</span>
        </div>

        <div class="resource-info-row">
          <span class="title">{{ t("events.detail.description") }}</span>
          <div class="value" v-html="eventDetail.description"></div>
        </div>
      </div>
      <div class="flex flex-col gap-4 bg-white rounded-xl shadow-md p-4 w-full">
        <div class="flex flex-col gap-4">
          <span class="font-extrabold">
            {{ t("events.detail.allocatedResources") }}
          </span>
          <InfoCardView
            v-for="item in eventDetail.resourceTypes"
            :data="item"
            :actions="[
              {
                icon: 'Eye',
                onClick: (r) =>
                  go(RouteName.ResourceDetail, { id: r.resourceId }),
              },
            ]"
          />
        </div>
        <div class="flex flex-col gap-4 py-2 border-t-1 border-t-[#E5E9EF]">
          <span class="font-extrabold">
            {{ t("events.detail.subEventAllocatedResources") }}
          </span>
          <div v-for="subEvent in eventDetail.subEvents">
            <InfoCardView
              v-for="item in subEvent.resourceTypes"
              :data="item"
              :actions="[
                {
                  icon: 'Eye',
                  onClick: (r) =>
                    go(RouteName.ResourceDetail, { id: r.resourceId }),
                },
              ]"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      class="flex flex-col gap-3 bg-white rounded-xl shadow-md p-4 w-full"
      v-if="(eventDetail.subEvents ?? []).length > 0"
    >
      <div class="grid grid-cols-3 items-center">
        <span class="font-extrabold text-md">
          {{ t("events.detail.subEvents.title") }}
        </span>
        <span class="font-semibold text-light-gray text-xs justify-self-center">
          {{ t("events.detail.subEvents.subTitle") }}
        </span>
      </div>
      <div class="flex flex-col gap-2">
        <SubEventCardView
          v-for="item in eventDetail.subEvents"
          :data="item"
          :edit-call-back="
            () => {
              selectedSubEvent = {
                name: item.name,
                startDate: item.startDate,
                endDate: item.endDate,
                location: item.location,
                id: item.id,
              };
              isDialogOpen = true;
            }
          "
          submit-button-icon="Eye"
          :submit-button-title="t('common.see')"
        />
        <CustomDialog
          v-model="isDialogOpen"
          :title="t('events.detail.subEvents.subEvent')"
          :is-submit-button-visible="false"
          @cancel="isDialogOpen = false"
        >
          <div class="flex flex-col gap-3">
            <CustomInput
              :title="t('common.name')"
              readonly
              v-model="eventDetail.name"
            />
            <CustomInput
              :title="t('events.detail.subEvents.subEventName')"
              v-model="selectedSubEvent!.name"
              readonly
            />
            <div class="flex flex-row gap-2 justify-between">
              <CustomDatePicker
                :title="t('events.detail.startDate')"
                :placeholder="t('events.detail.startDate')"
                v-model="selectedSubEvent!.startDate"
                readonly
              />
              <CustomDatePicker
                :title="t('events.detail.endDate')"
                :placeholder="t('events.detail.endDate')"
                v-model="selectedSubEvent!.endDate"
                readonly
              />
            </div>
            <CustomInput
              :title="t('events.detail.location')"
              v-model="selectedSubEvent!.location"
              readonly
            />
          </div>
        </CustomDialog>
      </div>
    </div>
    <div
      class="flex flex-col bg-white rounded-xl shadow-md p-4 w-full"
      v-if="timelineEvents.length > 0"
    >
      <div class="flex flex-row w-full justify-between">
        <span class="font-extrabold">
          {{ t("events.edit.timeline.title") }}
        </span>
      </div>
      <TimelineChart
        :events="timelineEvents"
        :callBack="(id: string) => go(RouteName.ResourceDetail, { id: id })"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from "@/core/composables/use-toast";
import IconManager from "@/core/IconManager.vue";
import InfoBox from "@/core/components/InfoBox.vue";
import "./EventDetailPage.scss";
import InfoCardView from "@/core/components/InfoCardView.vue";
import SubEventCardView from "@/modules/events/views/SubEventCardView.vue";
import { useI18n } from "vue-i18n";
import { eventsRemoteDataSource } from "@/core/models/global-variables";
import { EventState } from "../models/event-state";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { EventDetail } from "../models/event-detail";
import { RouteName } from "@/core/models/route-name";
import { format } from "date-fns";
import TimelineChart from "@/core/components/charts/TimelineChart.vue";
import CustomDatePicker from "@/core/components/CustomDatePicker.vue";
import CustomInput from "@/core/components/CustomInput.vue";
import CustomDialog from "@/core/components/CustomDialog.vue";
import { SubEvent } from "../models/sub-event";
import LoadingIcon from "@/core/components/LoadingIcon.vue";
import { Utils } from "@/core/models/utils";
import CustomButton from "@/core/components/CustomButton.vue";

const { t, locale } = useI18n();
const { showError } = useToast();

const props = defineProps<{
  id: string;
}>();

const router = useRouter();
const route = useRoute();
const eventDetail = ref<EventDetail | null>(null);
const selectedSubEvent = ref<SubEvent | null>(null);
const isLoading = ref<boolean>(true);
const isDialogOpen = ref(false);
const isDownloadingPDF = ref<boolean>(false);

const timelineEvents = computed(() =>
  eventDetail.value!.occupancyCalendarResources.map((item) => ({
    id: item.resourceId,
    title: item.name,
    events: item.intervals.flatMap((interval) =>
      interval.bars.map((bar) => ({
        id: interval.eventId,
        startDate: bar.startDate,
        endDate: bar.endDate,
        name: interval.name,
        color: Utils.getRandomColor(),
        tooltipContent: buildTooltipHtml(
          interval.eventId,
          interval.quantity,
          new Date(bar.startDate),
          new Date(bar.endDate),
        ),
      })),
    ),
  })),
);

onMounted(() => {
  loadInitialData();
});

async function loadInitialData() {
  isLoading.value = true;

  try {
    const [eventsRes] = await Promise.all([
      eventsRemoteDataSource.getEvent(route.params.id as string),
    ]);
    if (!eventsRes.isSuccess) {
      showError({
        message: eventsRes.detail!,
        title: eventsRes.title,
        statusCode: eventsRes.statusCode,
      });
    }
    eventDetail.value = eventsRes.data;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoading.value = false;
  }
}

function getLocalShortDate(date: Date) {
  return (
    date.getDate() +
    " " +
    date.toLocaleString(locale.value, { month: "short" }) +
    " " +
    date.getFullYear()
  );
}

function getLocalShortDateWithTime(date: Date) {
  return (
    date.getDate() +
    " " +
    date.toLocaleString(locale.value, { month: "short" }) +
    " " +
    date.getFullYear() +
    " " +
    format(date, "HH:mm")
  );
}

async function downloadPDF() {
  isDownloadingPDF.value = true;

  try {
    const response = await eventsRemoteDataSource.downloadPDF(props.id);
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    Utils.downloadBlob(response.data, "");
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isDownloadingPDF.value = false;
  }
}

function go(name: string, params: Record<string, any> = {}) {
  router.push({
    name,
    params,
  });
}

function buildTooltipHtml(
  eventId: string,
  quantity: number,
  startDate: Date,
  endDate: Date,
): string {
  return `
    <div style="font-size: 12px; line-height: 1.6;">
      <div><strong>${t("events.detail.timeline.tooltip.name")}:</strong> ${eventId}</div>
      <div><strong>${t("events.detail.timeline.tooltip.duration")}:</strong> ${Utils.formatDate(startDate, locale.value)} → ${Utils.formatDate(endDate, locale.value)}</div>
      <div><strong>${t("events.detail.timeline.tooltip.quantity")}:</strong> ${quantity}</div>
    </div>
  `;
}
</script>
